import React, {useState, useEffect, useRef, useContext} from 'react';
import {
  View,
  Pressable,
  ScrollView,
  Platform,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';

import NoReadings from '../../components/modules/NoReadings';
import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';
import {DLog} from '../../utils/HelperMethods';
import {errorStatusContext} from '../../contexts/ErrorStatusContext';
import {loadingPauseContext} from '../../contexts/LoadingPauseContext';
import Text from '../../components/elements/Text';
import Header from '../../components/elements/Header';
import Button from '../../components/elements/Button';
import {BUTTON_TYPES} from '../../utils/Constants';
import BloodSugarCardModule from '../../components/modules/BloodSugarCardModule';
import BloodSugarGraph from '../../components/modules/BloodSugarGraph';
import {fetchApi} from '../../utils/ApiHelper';
import {HTTP_METHODS, BLOOD_SUGAR_LEVEL} from '../../utils/URLConstants';
import {useNavigation} from '@react-navigation/native';
import ExportDataDateRangeModal from '../../components/modules/ExportDataDateRangeModal';
import BloodSugarCalendar from '../../components/modules/BloodSugarCalendar';
import GestureRecognizer from 'react-native-swipe-gestures';
import {
  onCalendarClose,
  config,
  updateFilterDate,
  DaysSelector,
  MiniCalendar,
  NotesSection,
  WebArrows,
} from './elements';
import {useOutletContext} from '../../utils/mockedWebLibs/react-router-dom';
import {useNavigate} from '../../utils/mockedWebLibs/react-router-dom';

const ViewBloodSugarHistory = ({
  route,
  homeMode,
  setShowDeviceSyncCTA,
  hypo,
  hyper,
  lastSyncedAt,
  setRightPanelComponent,
}) => {
  // DLog(`min: ${hypo} max: ${hyper}`);
  const navigation = useNavigation();
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const navigate = useNavigate(); //for web
  const [showExportModal, setShowExportModal] = useState(false);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [readings, setReadings] = useState({data: []});
  const [notes, setNotes] = useState({data: []});
  const [range, setRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    numDays: homeMode ? 1 : 7,
  });
  const [filterDate, setFilterDate] = useState(new Date().toISOString());
  const firstRender = useRef(true); //required so that we dont run the getData API twice
  const min = hypo
    ? parseInt(hypo)
    : route?.params?.hypo
    ? parseInt(route?.params?.hypo)
    : 0; //setting default at 0
  const max = hyper
    ? parseInt(hyper)
    : route?.params?.hyper
    ? parseInt(route?.params?.hyper)
    : 400; //setting default at 400

  const lpc = useContext(loadingPauseContext);
  const esc = useContext(errorStatusContext);

  const {setOpenManualEntry} = useOutletContext();

  // DLog('range is',range)

  useEffect(() => {
    // DLog('range update useEffect ViewBS history called with range as', range);
    if (firstRender.current) {
      firstRender.current = false;
    } else {
      getData();
      setFilterDate(range.startDate.toISOString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getData is re-created each render; refetch is intentionally keyed to range changes only
  }, [range]);

  useEffect(() => {
    //compute endDate if numDays updates, keep starting as the same
    setNumDays(range.numDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only initialization of the derived end date; setNumDays is re-created each render
  }, []);

  const setNumDays = numDays => {
    //when setting numdays, also update the end date
    let tempRange = {...range};
    tempRange.numDays = numDays;
    let currentEndDate = new Date(range.startDate);
    currentEndDate.setDate(currentEndDate.getDate() - numDays + 1);
    tempRange.endDate = currentEndDate;
    // DLog('updated range is', tempRange);
    setRange(tempRange);
  };

  const getData = () => {
    let toDate = new Date(range.startDate);
    toDate.setHours(23, 59, 59, 999);
    toDate = toDate.toISOString();
    let fromDate = new Date(range.endDate);
    fromDate.setHours(0, 0, 0, 0);
    fromDate = fromDate.toISOString();

    let params = {fromDate: fromDate, toDate: toDate};

    //add to params if home mode is true
    if (homeMode) {
      params.homeMode = true;
    }

    // DLog('params', HTTP_METHODS.GET,params);
    lpc.setVisible(true);
    fetchApi(HTTP_METHODS.GET, BLOOD_SUGAR_LEVEL, null, params)
      .then(res => {
        //DLog('got data', res.status, res.data.data);
        if (res.status === 200) {
          let nonNullData = (res.data?.data?.data || []).filter(
            d => d.diabetesMeasurement !== null,
          );
          let nonNullReadings = {...res.data.data};
          nonNullReadings.data = nonNullData;
          setReadings(nonNullReadings);
          setNotes(res.data.data);
        } //setReadings(res.data.data);
        else {
          setReadings({data: []});
          setNotes({data: []});
        }
        lpc.setVisible(false);
      })
      .catch(e => {
        // DLog('getData Error', e);
        setReadings({data: []});
        setNotes({data: []});
        if (e != 'No measurement records found') esc.showError(e);
        lpc.setVisible(false);
      });
  };

  useEffect(() => {
    if (homeMode) {
      readings.data.length > 0
        ? setShowDeviceSyncCTA(false)
        : setShowDeviceSyncCTA(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- CTA update is intentionally keyed to readings changes; setShowDeviceSyncCTA prop has no stability guarantee
  }, [readings]);

  const SubHeading = () => {
    return (
      <View style={styles.subHeading}>
        <View>
          <Text color={colors.textHeading}>My Glucose Levels</Text>
          <Text variation="Subtitle2Regular" color={colors.textTertiary}>
            {lastSyncedAt}
          </Text>
        </View>
        <Text
          variation="Body2"
          color={colors.link}
          lineHeight={21}
          onPress={() => {
            if (Platform.OS === 'web') {
              setOpenManualEntry({
                onGoBack: getData,
              });
            } else {
              navigation.navigate('AddGlucoseLevel', {
                onGoBack: getData,
              });
            }
          }}>
          Manual Input
        </Text>
      </View>
    );
  };

  const withScrollView = component => {
    return homeMode ? (
      component
    ) : (
      <ScrollView showsVerticalScrollIndicator={false}>{component}</ScrollView>
    );
  };

  //API call to save notes
  const saveNote = note => {
    // DLog('note is', note);
    const params = {
      measureAt: note.createAt,
      patientNote: note.noteText,
      heading: note.title,
    };
    // DLog('params are', params);
    lpc.setVisible(true);
    fetchApi(HTTP_METHODS.POST, BLOOD_SUGAR_LEVEL, params)
      .then(res => {
        // DLog(res.data);
        lpc.setVisible(false);
        getData();
      })
      .catch(e => {
        // DLog(e);
        lpc.setVisible(false);
        if (e.message === 'Duplicate data.') {
          esc.showError(
            'A reading/note already exists at the selected datetime. Please select new date-time or edit existing reading/note.',
          );
        } else {
          esc.showError(e);
        }
      });
  };

  const CardAndGraph = ({Card, Graph}) => {
    let WebGraph = {...Graph};
    WebGraph.props = {
      ...WebGraph.props,
      WebArrows: (
        <WebArrows
          onPressNext={() =>
            updateFilterDate(1, range, filterDate, setFilterDate)
          }
          onPressPrev={() =>
            updateFilterDate(-1, range, filterDate, setFilterDate)
          }
        />
      ),
    };
    if (Platform.OS === 'web') {
      return (
        <View style={styles.cardAndGraph}>
          <View style={styles.flexPoint5}>{Card}</View>
          <View style={styles.flexPoint5}>{WebGraph}</View>
        </View>
      );
    }

    return (
      <>
        {Card}
        <GestureRecognizer
          key="blood-sugar-graph"
          onSwipeLeft={state =>
            updateFilterDate(1, range, filterDate, setFilterDate)
          }
          onSwipeRight={state =>
            updateFilterDate(-1, range, filterDate, setFilterDate)
          }
          config={config}
          style={styles.fullFlex}>
          {Graph}
        </GestureRecognizer>
      </>
    );
  };

  useEffect(() => {
    if (Platform.OS === 'web' && !homeMode) {
      setRightPanelComponent(
        <NotesSection
          navigation={navigation}
          notes={notes}
          saveNote={saveNote}
        />,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- saveNote is re-created each render; right-panel refresh is intentionally keyed to notes changes only
  }, [notes]);

  // if (Platform.OS === 'web') {
  //   return <NoReadings />;
  // } else
  return (
    <SafeAreaView style={styles.fullFlex}>
      {!homeMode && (
        <>
          <View style={styles.headerStyle}>
            {Platform.OS !== 'web' && (
              <Header
                title="Blood Sugar History"
                onPressLeftIcon={() => {
                  navigation.goBack();
                }}
              />
            )}
          </View>
          <DaysSelector range={range} setNumDays={setNumDays} />
        </>
      )}

      {readings.data.length === 0 && homeMode ? (
        <>
          <SubHeading />
          <MiniCalendar
            homeMode={homeMode}
            range={range}
            setRange={setRange}
            setCalendarVisible={setCalendarVisible}
          />
          <NoReadings />
        </>
      ) : (
        withScrollView(
          <>
            {homeMode && <SubHeading />}
            <MiniCalendar
              homeMode={homeMode}
              range={range}
              setRange={setRange}
              setCalendarVisible={setCalendarVisible}
            />
            <CardAndGraph
              Card={
                <BloodSugarCardModule
                  homeMode={homeMode}
                  min={min}
                  max={max}
                  readings={readings}
                />
              }
              Graph={
                <BloodSugarGraph
                  homeMode={homeMode}
                  min={min}
                  max={max}
                  readings={readings}
                  filterDate={filterDate}
                />
              }
            />
            {!homeMode && (
              <>
                <View style={styles.buttonContainerStyle}>
                  <Button
                    title="Add Manual Input"
                    titleColor={colors.link}
                    buttonType={BUTTON_TYPES.Plain}
                    style={styles.buttonStyle}
                    onPress={() => {
                      if (Platform.OS === 'web') {
                        setOpenManualEntry({
                          onGoBack: getData,
                        });
                      } else {
                        navigation.navigate('AddGlucoseLevel', {
                          onGoBack: getData,
                        });
                      }
                    }}
                  />
                </View>

                <ExportDataDateRangeModal
                  visible={showExportModal}
                  onClose={setShowExportModal}
                />

                <View style={styles.padding16}>
                  <View style={styles.allMeasurementsStyle}>
                    <Text
                      color={colors.textHeading}
                      variation="Headline4SemiBold">
                      All Measurements
                    </Text>
                    <Pressable
                      onPress={() => {
                        setShowExportModal(true);
                      }}>
                      <Text variation="Body2" lineHeight={21}>
                        Export
                      </Text>
                    </Pressable>
                  </View>
                  <Text variation="Subtitle2Regular" color={colors.textTertiary}>
                    {range.numDays} Days Average
                  </Text>
                </View>

                <BloodSugarGraph
                  homeMode={homeMode}
                  min={min}
                  max={max}
                  allMeasurements={true}
                  readings={readings}
                  numDays={range.numDays}
                  startDate={range.startDate.toISOString()}
                />

                <View style={styles.buttonContainerStyle}>
                  <Button
                    title="View All Glucose Levels"
                    titleColor={colors.link}
                    buttonType={BUTTON_TYPES.Plain}
                    style={styles.buttonStyle}
                    onPress={() =>
                      Platform.OS === 'web'
                        ? navigate('/view-all-readings')
                        : navigation.navigate('ViewCompleteHistory', {
                            onGoBack: getData,
                          })
                    }
                  />
                </View>

                <BloodSugarCalendar
                  visible={calendarVisible}
                  onClose={dates =>
                    onCalendarClose(dates, range, setRange, setCalendarVisible)
                  }
                />
                {Platform.OS !== 'web' && (
                  <NotesSection
                    navigation={navigation}
                    notes={notes}
                    saveNote={saveNote}
                  />
                )}
              </>
            )}
          </>,
        )
      )}
    </SafeAreaView>
  );
};

export default ViewBloodSugarHistory;
